import { ExportProgressReporter } from "../components/CustomButton";
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import PeopleIcon from "@mui/icons-material/People";
import AccountBalanceWalletOutlinedIcon from "@mui/icons-material/AccountBalanceWalletOutlined";
import ReceiptLongOutlinedIcon from "@mui/icons-material/ReceiptLongOutlined";
import AssessmentOutlinedIcon from "@mui/icons-material/AssessmentOutlined";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import { toast } from "react-toastify";
import CustomLabel from "../components/CustomLabel";
import PageHeader from "../components/PageHeader";
import {
  ADMIN_DATA_SUBTITLES,
  adminNavItemSx,
  adminNavPanelSx,
  adminPageSx,
  adminNavTitleSx,
} from "../components/adminData/adminNavStyles";
import CustomTable from "../components/table/CustomTable";
import SearchHeaderContainer from "../components/table/SearchHeaderContainer";
import Actions from "../components/Actions";
import GenericFormDialog, { Field } from "../components/GenericFormDialog";
import ConfirmDialog from "../components/ConfirmDialog";
import { COLORS } from "../assets/theme";
import { countries } from "../assets/utils/countries";
import { IPageable, Params } from "../types/table/interfaces.type";
import {
  deleteClient,
  downloadClientsCsv,
  getClientsPage,
  updateClient,
} from "../services/ClientsService";
import { useDebouncedValue } from "../hooks/useDebouncedValue";
import { getAllBusinesses } from "../services/VenueService";
import PinAccessDialog from "../components/PinAccessDialog";
import {
  getCachedPinAccess,
  subscribeToPinAccess,
} from "../utils/pinAccessCache";
import AddIcon from "@mui/icons-material/Add";
import AddClientDialog from "../components/AddClientDialog";
import { formatTableDate } from "../utils/tableDateFormat";

const showErrorToast = (message: any) =>
  toast.error(String(message), { toastId: String(message) });

type AdminTab = "clients" | "finance" | "expenses" | "reports" | "notifications";

interface AdminClientRow {
  id: string;
  fullName: string;
  email: string;
  countryCode?: string;
  phoneNumber?: string;
  status?: string;
  inApp?: boolean;
  adminUserId?: string;
  branchName?: string;
  businessId?: string;
  userId?: string;
  gender?: string;
  dob?: string;
}

const normalizeCountryCodeValue = (value?: unknown): string => {
  if (value === undefined || value === null) return "";
  const stringValue = typeof value === "string" ? value : String(value);
  const match = stringValue.match(/^\s*(\+?\d+)/);
  const dialCode = match ? match[1] : stringValue.trim();
  if (!dialCode) return "";
  return dialCode.startsWith("+") ? dialCode : `+${dialCode}`;
};

const normalizeActiveStatus = (value?: unknown): "verified" | "unverified" => {
  if (typeof value === "boolean") return value ? "verified" : "unverified";
  if (typeof value === "number") return value === 1 ? "verified" : "unverified";
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "verified") return "verified";
    if (normalized === "unverified") return "unverified";
    if (
      normalized === "active" ||
      normalized === "enabled" ||
      normalized === "true"
    ) {
      return "verified";
    }
    if (
      normalized === "inactive" ||
      normalized === "disabled" ||
      normalized === "false"
    ) {
      return "unverified";
    }
  }
  return "unverified";
};

const normalizeFromApp = (value?: unknown): boolean | undefined => {
  if (value === undefined || value === null) return undefined;
  if (typeof value === "boolean") return value;
  if (typeof value === "number") return value === 1;
  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    if (normalized === "true" || normalized === "1" || normalized === "yes") {
      return true;
    }
    if (normalized === "false" || normalized === "0" || normalized === "no") {
      return false;
    }
  }
  return undefined;
};

const toIsoDateString = (value: any): string => {
  if (!value) return "";
  try {
    const d = value instanceof Date ? value : new Date(value);
    if (isNaN(d.getTime())) return "";
    return d.toISOString();
  } catch {
    return "";
  }
};

const formatDateOnly = (value: any): string => {
  if (!value) return "";
  const formatted = formatTableDate(value);
  return formatted === "-" ? String(value) : formatted;
};

const ADMIN_CLIENTS_PAGE_SIZE = 30;

const toAdminClientRow = (item: any): AdminClientRow => ({
  id: String(item.id),
  fullName: item.full_name || "",
  email:
    item.user?.email ||
    item.email ||
    item.user_email ||
    item.contact?.email ||
    "",
  countryCode: normalizeCountryCodeValue(item.country),
  phoneNumber: item.phone || "",
  status: normalizeActiveStatus(item.active),
  inApp: normalizeFromApp(item.inApp ?? item.in_app),
  adminUserId: item.user_id,
  branchName: "",
  businessId: "",
  userId: item.user_id,
  gender: item.gender || "",
  dob: toIsoDateString(
    item.DOB ||
      item.dob ||
      item.date_of_birth ||
      item.birthdate ||
      item.birth_date,
  ),
});

const AdminData: React.FC = () => {
  const navigate = useNavigate();
  const activeTab: AdminTab = "clients";
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [search, setSearch] = useState("");
  const [businesses, setBusinesses] = useState<any[]>([]);
  const [businessFilter, setBusinessFilter] = useState<string>("all");
  const [inAppFilter, setInAppFilter] = useState<string>("all");
  const [orgClients, setOrgClients] = useState<AdminClientRow[]>([]);
  const [importedClients, setImportedClients] = useState<AdminClientRow[]>(
    [],
  );
  const [loadingClients, setLoadingClients] = useState(false);
  const [clientEditOpen, setClientEditOpen] = useState(false);
  const [editingClient, setEditingClient] = useState<AdminClientRow | null>(
    null,
  );
  const [clientConfirmOpen, setClientConfirmOpen] = useState(false);
  const [clientToDeleteId, setClientToDeleteId] = useState<
    string | number | null
  >(null);
  const [clientRefreshToken, setClientRefreshToken] = useState(0);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const [financeUnlocked, setFinanceUnlocked] = useState(() =>
    getCachedPinAccess(),
  );
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pendingFinancePath, setPendingFinancePath] = useState<string | null>(
    null,
  );

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [addClientDialogOpen, setAddClientDialogOpen] = useState(false);


  useEffect(() => {
    const fetchBusinesses = async () => {
      try {
        const response = await getAllBusinesses();
        const businessList = Array.isArray(response?.data)
          ? response.data
          : Array.isArray(response)
            ? response
            : [];
        setBusinesses(businessList);
      } catch (error) {
        console.error("Failed to fetch businesses:", error);
        setBusinesses([]);
      }
    };

    fetchBusinesses();
  }, []);

  useEffect(() => {
    const unsubscribe = subscribeToPinAccess((value) => {
      setFinanceUnlocked(value);
      if (!value) {
        setPinDialogOpen(true);
      }
    });
    return unsubscribe;
  }, []);


  const requestFinanceNav = useCallback(
    (path: string) => {
      if (financeUnlocked) {
        navigate(path);
        return;
      }
      setPendingFinancePath(path);
      setPinDialogOpen(true);
    },
    [financeUnlocked, navigate],
  );

  const debouncedSearch = useDebouncedValue(search, 400);

  // One page from the server per request: search, business and app filters
  // all run there.
  const fetchClients = useCallback(
    async (
      params: Params<AdminClientRow>,
    ): Promise<IPageable<AdminClientRow>> => {
      const page = Number(params.page) || 0;
      const size = Number(params.size) || ADMIN_CLIENTS_PAGE_SIZE;
      setLoadingClients(true);
      try {
        const result = await getClientsPage({
          businessId: businessFilter,
          page,
          size,
          search: String(params.search || ""),
          inApp:
            inAppFilter === "true" || inAppFilter === "false"
              ? inAppFilter
              : undefined,
        });
        const mapped = (result?.content ?? []).map(toAdminClientRow);
        // Imported CSV rows are not saved, so they only live in this table.
        const content = page === 0 ? [...importedClients, ...mapped] : mapped;
        setOrgClients(content);
        return {
          content,
          number: page,
          size,
          totalElements: result?.totalElements ?? content.length,
          totalPages: result?.totalPages ?? 1,
        };
      } catch (error) {
        setOrgClients([]);
        throw error;
      } finally {
        setLoadingClients(false);
      }
    },
    [businessFilter, importedClients, inAppFilter],
  );

  const exportClientCsv = useCallback(async (reportProgress?: ExportProgressReporter) => {
    try {
      await downloadClientsCsv(
        {
          businessId: businessFilter,
          search: debouncedSearch,
          inApp:
            inAppFilter === "true" || inAppFilter === "false"
              ? inAppFilter
              : undefined,
        },
        reportProgress,
      );
    } catch (error: any) {
      toast.error(error?.message || "Failed to export clients.");
    }
  }, [businessFilter, debouncedSearch, inAppFilter]);

  const importCsv = useCallback(
    (file: File) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result as string;
        if (!text) return;
        const lines = text.split("\n").filter((line) => line.trim());
        if (lines.length < 2) return;
        const header = lines[0]
          .split(",")
          .map((h) => h.replace(/"/g, "").trim());
        const dataLines = lines.slice(1);
          const newRows = dataLines.map((line, idx) => {
            const values = line.split(",").map((v) => v.replace(/"/g, "").trim());
            const obj: any = {};
            header.forEach((h, i) => {
              obj[h] = values[i] || "";
          });
          obj.id = obj.id || `import-${Date.now()}-${idx}`;
          return {
            id: String(obj.id),
            fullName: obj.fullName || obj.name || "",
            email: obj.email || "",
            countryCode: obj.countryCode || obj.country_code || "",
            phoneNumber: obj.phoneNumber || obj.phone || "",
            status: obj.status || "",
            inApp: normalizeFromApp(obj.inApp ?? obj.in_app ?? obj.fromApp ?? obj.from_app),
            gender: obj.gender || "",
            dob: toIsoDateString(
              obj.dob || obj.date_of_birth || obj.birthdate || obj.birth_date,
            ),
          } as AdminClientRow;
        });
        setImportedClients((prev) => [...newRows, ...prev]);
        toast.success("CSV imported into the table");
      };
      reader.readAsText(file);
    },
    [],
  );

  const openClientEditForId = useCallback(
    (id: string | number) => {
      const found = orgClients.find((r) => String(r.id) === String(id));
      if (found) {
        setEditingClient(found);
        setClientEditOpen(true);
      }
    },
    [orgClients],
  );

  const handleClientDelete = useCallback(async () => {
    if (!clientToDeleteId) return;
    try {
      await deleteClient(String(clientToDeleteId));
      toast.success("Client deleted");
      setClientRefreshToken((prev) => prev + 1);
    } catch (error: any) {
      console.error("Delete client failed", error);
      const message =
        error?.response?.data?.message ||
        error?.response?.data?.error ||
        error?.message ||
        "Failed to delete client";
      showErrorToast(message);
    } finally {
      setClientConfirmOpen(false);
      setClientToDeleteId(null);
    }
  }, [clientToDeleteId]);

  const handleClientEditSubmit = useCallback(
    async (values: Record<string, string>) => {
      if (!editingClient) return;

      try {
        const payload = {
          full_name: values.fullName,
          email: values.email || undefined,
          country: values.countryCode || undefined,
          phone: values.phoneNumber || undefined,
          active: normalizeActiveStatus(values.status) === "verified",
          gender: values.gender,
          DOB: values.dob ? new Date(values.dob).toISOString() : undefined,
        };

        await updateClient(editingClient.id, payload);

        toast.success("Client updated");
        setClientEditOpen(false);
        setEditingClient(null);
        setClientRefreshToken((prev) => prev + 1);
      } catch (error: any) {
        console.error("Update client failed", error);
        toast.error(
          error?.response?.data?.message || "Failed to update client",
        );
      }
    },
    [editingClient],
  );

  const clientActions = Actions({
    handleOpenEditModal: { handler: openClientEditForId },
    onDelete: {
      handler: (id: number | string) => {
        setClientToDeleteId(id);
        setClientConfirmOpen(true);
      },
      /*onDetails: {
      handler: (id: number | string) => {
        const clientId = String(id);
        navigate(`/dashboard/${businessId}/clients/details/${clientId}`);
      },
    },*/
    },
  });

  const navItems: { key: AdminTab; label: string; icon: React.ReactNode }[] = [
    { key: "clients", label: "Clients", icon: <PeopleIcon /> },
    {
      key: "finance",
      label: "Invoices",
      icon: <AccountBalanceWalletOutlinedIcon />,
    },
    { key: "expenses", label: "Expenses", icon: <ReceiptLongOutlinedIcon /> },
    { key: "reports", label: "Reports", icon: <AssessmentOutlinedIcon /> },
    {
      key: "notifications",
      label: "Notification Devices",
      icon: <NotificationsActiveOutlinedIcon />,
    },
  ];
  const activeTabLabel =
    navItems.find((item) => item.key === activeTab)?.label || "Admin Data";
  const clientFormFields: Field[] = [
    { name: "fullName", label: "Full Name", required: true },
    { name: "email", label: "Email" },
    {
      name: "countryCode",
      label: "Country Code",
      type: "dropdown",
      options: countries.map((c) => ({
        label: `+${c.phone} - ${c.label}`,
        value: `+${c.phone}`,
      })),
      searchable: true,
      required: true,
    },
    { name: "phoneNumber", label: "Phone Number", required: true },
    {
      name: "status",
      label: "Status",
      type: "dropdown",
      options: [
        { label: "Verified", value: "verified" },
        { label: "Unverified", value: "unverified" },
      ],
    },
    {
      name: "gender",
      label: "Gender",
      type: "dropdown",
      options: [
        { label: "Male", value: "MALE" },
        { label: "Female", value: "FEMALE" },
      ],
    },
    { name: "dob", label: "Date of Birth", type: "date" },
  ];

  return (
    <Box
      sx={adminPageSx}
    >
      <Box
        sx={adminNavPanelSx(sidebarCollapsed)}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: sidebarCollapsed ? "center" : "space-between",
            px: sidebarCollapsed ? 0 : 0.5,
            mb: 1,
          }}
        >
          {!sidebarCollapsed && (
            <CustomLabel text="Admin Data" variant="h6" sx={adminNavTitleSx} />
          )}
          <IconButton
            size="small"
            onClick={() => setSidebarCollapsed((prev) => !prev)}
            sx={{ color: COLORS.MainColor }}
          >
            {sidebarCollapsed ? <MenuIcon /> : <ChevronLeftIcon />}
          </IconButton>
        </Box>

        <List disablePadding>
          {navItems.map((item) => {
            const selected = activeTab === item.key;
            return (
              <Tooltip
                key={item.key}
                title={sidebarCollapsed ? item.label : ""}
                placement="right"
              >
                <ListItemButton
                  selected={selected}
                  onClick={() => {
                    if (item.key === "finance") {
                      requestFinanceNav("/all-data/invoices");
                    } else if (item.key === "expenses") {
                      requestFinanceNav("/all-data/expenses");
                    } else if (item.key === "reports") {
                      requestFinanceNav("/all-data/reports");
                    } else if (item.key === "notifications") {
                      navigate("/all-data/notification-devices");
                    }
                  }}
                  sx={adminNavItemSx(selected, sidebarCollapsed)}
                >
                  <ListItemIcon
                    sx={{
                      minWidth: sidebarCollapsed ? "auto" : 32,
                      color: "inherit",
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  {!sidebarCollapsed && (
                    <ListItemText
                      primary={item.label}
                      primaryTypographyProps={{ fontWeight: 600, fontSize: "0.9rem" }}
                    />
                  )}
                </ListItemButton>
              </Tooltip>
            );
          })}
        </List>
      </Box>

      <Box
        sx={{
          flex: 1,
          px: { xs: 1.5, sm: 2.5 },
          // Page is pulled up under the header (adminPageSx); give it back here.
          pt: { xs: 3, sm: 5 },
          pb: { xs: 2, sm: 2.5 },
          maxWidth: "100%",
          minWidth: 0,
        }}
      >
        <PageHeader
          title={activeTabLabel}
          subtitle={ADMIN_DATA_SUBTITLES[activeTab]}
          icon={navItems.find((item) => item.key === activeTab)?.icon}
        />

        <Box
          sx={{
            backgroundColor: COLORS.WhiteBackground,
            borderRadius: 2,
            p: { xs: 1.5, sm: 2 },
            boxShadow: "0px 4px 12px rgba(0,0,0,0.04)",
          }}
        >
          <SearchHeaderContainer
            search={search}
            setSearch={setSearch}
            selectConfig={{
              options: [
                { label: "All", value: "all" },
                ...businesses.map((b) => ({
                  label: b.name,
                  value: String(b.id),
                })),
              ],

              value: businessFilter,
              onChange: (v: any) => setBusinessFilter(String(v)),
              label: "Business",
              placeholder: "Select Business",
              size: "medium",
              searchable: false,
              multiSelect: false,
              sx: {
                backgroundColor: COLORS.WhiteBackground,
              },
            }}
            selectConfig2={{
              options: [
                { label: "All", value: "all" },
                { label: "true", value: "true" },
                { label: "false", value: "false" },
              ],

              value: inAppFilter,
              onChange: (v: any) => setInAppFilter(String(v)),
              label: "From App",
              placeholder: "Select Value",
              size: "medium",
              searchable: false,
              multiSelect: false,
              sx: {
                backgroundColor: COLORS.WhiteBackground,
              },
            }}
            buttonConfig={[
              {
                size: "small",
                bgColor: COLORS.MainColor,
                startIcon: <AddIcon />,
                onClick: () => {
                  setAddClientDialogOpen(true);
                },
                textColor: COLORS.WhiteText,
                minHeight: 36,
                maxHeight: 36,
                sx: {
                  width: { xs: "96%", sm: 36 },
                  minWidth: { sm: 36 },
                  marginRight: { xs: 1.5, sm: 0 },
                },
              },
              {
                label: "Import CSV",
                onClick: () => fileInputRef.current?.click(),
                startIcon: <FileDownloadIcon />,
                variant: "contained",
                bgColor: COLORS.MainColor,
                textColor: "#fff",
                showExportProgress: true,
                sx: {
                  width: { xs: "100%", sm: "auto" },
                  marginRight: { xs: 1.5, sm: 0 },
                },
              },
              {
                label: "Export CSV",
                onClick: (reportProgress) => exportClientCsv(reportProgress),
                startIcon: <FileDownloadIcon />,
                variant: "contained",
                bgColor: COLORS.MainColor,
                textColor: "#fff",
                showExportProgress: true,
                sx: {
                  width: { xs: "100%", sm: "auto" },
                  marginRight: { xs: 1.5, sm: 0 },
                },
              },
            ]}
          />
          <input
            type="file"
            ref={fileInputRef}
            accept=".csv,text/csv"
            style={{ display: "none" }}
            onChange={(e) => {
              const file = e.target.files?.[0];
              if (file) {
                importCsv(file);
                e.target.value = "";
              }
            }}
          />

          <Box sx={{ mt: 2 }}>
            <CustomTable<AdminClientRow>
              filters={{
                page: 0,
                size: ADMIN_CLIENTS_PAGE_SIZE,
                search: debouncedSearch,
                businessFilter,
                inAppFilter,
              }}
              refreshSignal={clientRefreshToken}
              fields={[
                { key: "fullName", header: "Full Name" },
                {
                  key: "email",
                  header: "Email",
                  transform: (row) => row.email || "-",
                },
                { key: "gender", header: "Gender" },
                {
                  key: "dob",
                  header: "Date of Birth",
                  transform: (row) => formatDateOnly(row?.dob),
                },
                { key: "countryCode", header: "Country Code" },
                { key: "phoneNumber", header: "Phone Number" },
                {
                  key: "inApp",
                  header: "From App",
                  transform: (row) =>
                    row.inApp === true
                      ? "true"
                      : row.inApp === false
                        ? "false"
                        : "-",
                },
                { key: "status", header: "Status" },
              ]}
              fetchFunc={fetchClients}
              loading={loadingClients}
              actions={clientActions}
            />
          </Box>
        </Box>

      </Box>

      <GenericFormDialog
        open={clientEditOpen}
        onClose={() => {
          setClientEditOpen(false);
          setEditingClient(null);
        }}
        title="Edit Client"
        fields={clientFormFields}
        initialValues={
          editingClient
            ? {
                fullName: editingClient.fullName || "",
                email: editingClient.email || "",
                countryCode: normalizeCountryCodeValue(
                  editingClient.countryCode || "",
                ),
                phoneNumber: editingClient.phoneNumber || "",
                status: normalizeActiveStatus(editingClient.status || ""),
                gender: editingClient.gender
                  ? String(editingClient.gender).toUpperCase()
                  : "",
                dob: editingClient.dob || "",
              }
            : undefined
        }
        onSubmit={handleClientEditSubmit}
      />

      <ConfirmDialog
        open={clientConfirmOpen}
        onClose={() => setClientConfirmOpen(false)}
        onConfirm={handleClientDelete}
        title="Confirm delete"
        message="Are you sure you want to delete this client?"
      />
      <PinAccessDialog
        open={pinDialogOpen}
        onClose={() => {
          setPinDialogOpen(false);
          setPendingFinancePath(null);
        }}
        onVerified={() => {
          setFinanceUnlocked(true);
          setPinDialogOpen(false);
          if (pendingFinancePath) {
            navigate(pendingFinancePath);
            setPendingFinancePath(null);
          }
        }}
        title="Finance access"
        description="Enter the PIN to access admin finance pages."
      />
       <AddClientDialog
  open={addClientDialogOpen}
  onClose={() => setAddClientDialogOpen(false)}
  onCreated={() => {
    setClientRefreshToken((prev) => prev + 1);
  }}
/>
    </Box>
  );
};

export default AdminData;
